// ---------------------------------------------------------------------------
// Prezio Onboard Widget — NavHeader
// ---------------------------------------------------------------------------
// Left: Back button (shown when there are completed steps).
// Right: Language menu (once a country is chosen) and Close button.
// Logo display is handled by LogoBar (rendered below ProgressBar).
// ---------------------------------------------------------------------------

import { useSessionState, useSessionActions } from '@/hooks/useSession';
import { t } from '@/i18n';
import { getVisibleSteps } from '@/utils/constants';
import { LanguageMenu } from '@/components/shell/LanguageMenu';
import styles from './NavHeader.module.css';

interface NavHeaderProps {
  onClose?: () => void;
}

export function NavHeader({ onClose }: NavHeaderProps) {
  const { session, isSubmitting } = useSessionState();
  const { cancelSession, goBack } = useSessionActions();

  const hasCompletedSteps = (session?.progress.completed_steps.length ?? 0) > 0;
  const hasProgress = (session?.progress.percentage ?? 0) > 0;
  const isActive = session?.status === 'created' || session?.status === 'in_progress';

  // Determine if user is past the first *visible* step (excluding predefined
  // steps that the user never sees). The backend doesn't always populate
  // progress.completed_steps, so we derive it from current_step position in
  // the visible step order (same approach ProgressBar uses).
  const visibleSteps = getVisibleSteps(session?.predefined_steps);
  const currentIdx = session?.current_step ? visibleSteps.indexOf(session.current_step) : 0;
  const isPastFirstStep =
    hasCompletedSteps || hasProgress || currentIdx > 0 || Object.keys(session?.collected_data ?? {}).length > 0;

  // Show back button on all active steps; disable on first step or while submitting
  const showBackButton = isActive;
  const canGoBack = isPastFirstStep && !isSubmitting;

  const handleBack = () => {
    void goBack().catch(() => {
      // Errors are already dispatched to state by goBack.
    });
  };

  const handleClose = async () => {
    // cancelSession will call onCancel callback internally after success
    try {
      await cancelSession('user_closed');
    } catch {
      // If API call fails, still notify host so they can clean up
      onClose?.();
    }
  };

  return (
    <header className={styles['header']}>
      {/* Left: Back button */}
      {showBackButton ? (
        <button
          type="button"
          className={styles['iconButton']}
          onClick={handleBack}
          disabled={!canGoBack}
          aria-label={t('common.goBack')}
        >
          <BackIcon />
        </button>
      ) : (
        <div className={styles['ghost']} aria-hidden="true" />
      )}

      {/* Right: Language menu + Close button */}
      <div className={styles['actions']}>
        <LanguageMenu />
        <button
          type="button"
          className={styles['iconButton']}
          onClick={() => void handleClose()}
          disabled={isSubmitting}
          aria-label={t('common.close')}
        >
          <CloseIcon />
        </button>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons (keeps bundle small — no icon library needed)
// ---------------------------------------------------------------------------

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M12.5 15L7.5 10L12.5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M15 5L5 15M5 5L15 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
